import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Carteira do consultor (at-32, Plano C) — tela `/carteira`.
 *
 * Usa o cliente `http` central (at-17): o Bearer é injetado pelo interceptor,
 * nunca montado aqui. O endpoint é 100% local (não chama a Hinova), então a
 * tela continua abrindo com o SGA fora do ar.
 */

export type OrigemRegistro = 'SAAC' | 'SGA_IMPORTADO';
export type OrigemFiltro = 'TODOS' | OrigemRegistro;
export type SituacaoEnvioFiltro = 'TODOS' | 'PENDENTE' | 'ENVIADO';
export type ProximoPassoVistoria = 'CHECKLIST' | 'ASSINATURA' | 'LAUDO' | null;

export interface VistoriaDaCarteira {
  id: string;
  data: string;
  /** `null` = concluída. Vem do `resolverProximoPasso`, a mesma fonte do painel /vistoria. */
  proximoPasso: ProximoPassoVistoria;
  concluida: boolean;
  laudoEnviadoSga: boolean;
  fotosEnviadasSga: boolean;
  quantidadeFotos: number;
}

export interface VeiculoDaCarteira {
  id: string;
  placa: string;
  /** at-46: motocicleta elétrica — a "placa" é a identificação gerada pelo SAAC. */
  bikeEletrica?: boolean;
  /** at-81: veículo 0km — a `placa` é a identificação provisória e a tela oferece "Informar placa". */
  zeroKm?: boolean;
  modelo: string;
  /** Usados ao reidratar o veículo para o fluxo de vistoria. */
  anoModelo: number;
  anoFabricacao: number;
  codigoVeiculo: string | null;
  addedToSga: boolean;
  /** Situação no SAAC (at-37/item 39): ATIVO ou SUBSTITUIDO. */
  situacao: 'ATIVO' | 'SUBSTITUIDO';
  /** Atalho do backend para `situacao === 'SUBSTITUIDO'`. */
  substituido: boolean;
  origem: OrigemRegistro;
  situacaoSga: string | null;
  produtos: number;
  termosLocais: number;
  vistoria: VistoriaDaCarteira | null;
  /** A vistoria exige o associado já no SGA — a tela desabilita a ação em vez de deixar falhar. */
  podeVistoriar: boolean;
  tipoVeiculo: string | null;
}

export interface AssociadoDaCarteira {
  id: string;
  nomeCompleto: string;
  cpfCnpj: string;
  codigoAssociado: string | null;
  addedToSga: boolean;
  origem: OrigemRegistro;
  situacaoSga: string | null;
  cotacoesNoMes: number;
  limiteCotacoesMes: number;
  veiculos: VeiculoDaCarteira[];
}

export interface TotaisCarteira {
  associados: number;
  veiculos: number;
  veiculosPendentesSga: number;
  vistoriasPendentes: number;
  importadosDoSga: number;
}

export interface Carteira {
  totalItems: number;
  page: number;
  limit: number;
  /** Contadores GLOBAIS — não acompanham a busca nem os filtros (at-10/at-28). */
  totais: TotaisCarteira;
  items: AssociadoDaCarteira[];
}

export interface FiltrosCarteira {
  page?: number;
  limit?: number;
  busca?: string;
  origem?: OrigemFiltro;
  situacao?: SituacaoEnvioFiltro;
}

export const fetchCarteira = async (filtros: FiltrosCarteira = {}): Promise<Carteira> => {
  try {
    const busca = filtros.busca?.trim();

    const { data } = await http.get<Carteira>('/carteira', {
      params: {
        page: filtros.page ?? 1,
        limit: filtros.limit ?? 10,
        // Só manda o que tem valor: parâmetro vazio viraria filtro inválido (400).
        ...(busca ? { busca } : {}),
        ...(filtros.origem && filtros.origem !== 'TODOS' ? { origem: filtros.origem } : {}),
        ...(filtros.situacao && filtros.situacao !== 'TODOS' ? { situacao: filtros.situacao } : {}),
      },
    });

    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível carregar sua carteira.'));
  }
};

/**
 * Resumo do "Atualizar com o SGA" (at-32, Plano F).
 *
 * SGA fora do ar NÃO vira erro aqui: volta como `sgaIndisponivel` dentro do
 * resumo, porque a carteira precisa seguir utilizável sem ele.
 */
export interface ResumoAtualizacaoCarteira {
  associadosVerificados: number;
  naoVerificados: number;
  placasImportadas: string[];
  veiculosImportados: number;
  divergenciasAbertas: number;
  consultasSga: number;
  /** Parou no teto de chamadas: basta clicar de novo para continuar. */
  limiteAtingido: boolean;
  sgaIndisponivel: boolean;
  falhas: string[];
  mensagem: string;
}

export const atualizarCarteiraComSga = async (): Promise<ResumoAtualizacaoCarteira> => {
  try {
    const { data } = await http.post<ResumoAtualizacaoCarteira>('/carteira/atualizar-com-sga');
    return data;
  } catch (error) {
    throw new Error(
      extrairMensagemErro(error, 'Não foi possível atualizar a carteira com o SGA.'),
    );
  }
};
